import { NewUnitsHealthIcon } from 'components/HealthIcon';
import { setValueState, verifyArrayParameters, verifyArrayParametersWithNumber } from 'helpers/genericHelper';
import { getUserProfile, refreshUserProfile } from 'helpers/userProfile';
import { useStateVar } from 'helpers/useStateVar';
import moment, { Moment } from 'moment';
import { AnalysisLayout } from 'pages/Analysis/AnalysisLayout';
import { apiCall } from 'providers';
import {
  ReactElement, useEffect, useRef, useState,
} from 'react';
import { CSVLink } from 'react-csv';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Box, Flex } from 'reflexbox';
import { colors } from 'styles/colors';
import '~/assets/css/ReactTags.css';
import {
  DataTable,
  EmptyWrapper,
  Loader,
} from '~/components';
import { OrderableHeaderCell, TableColumn } from '~/components/DataTable';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ModernizationUnitIcon,
  UnitIcon,
} from '../../icons';
import { FilterItem } from './AnalysisFilters';
import {
  DesktopTable,
  ExpandAll,
  ExpandBtn,
  RowHealth,
  RowTemp,
  StyledLink,
  StyledSpan,
  TempColor,
} from './styles';
import { UtilFilter } from './Utilities/UtilityFilter';

import { withTransaction } from '@elastic/apm-rum-react';
import { TooltipContainer } from 'components/ConnectedDevices/styles';
import { IntroRemoteControlModal } from 'components/ModalsRemoteControl/IntroductionModal';
import { formatNumberWithFractionDigits } from 'helpers/thousandFormatNumber';
import { handleUnitStatusString } from 'helpers/unitStatus';
import { UnitStatus, UnitStatusKey } from 'providers/types/unit';
import { FaTools } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { FilterStateRoute, handleOverviewStateFilters } from '../NewOverview/helpers/filters';
import { AnalysisEmpty } from './AnalysisEmpty';
import { TableItemCell } from './Units/UnitDetail/UnitDetailDACsDAMs/components/MachineTable/styles';

function handleUnitStatusIcon(production: string): ReactElement | null {
  switch (production) {
    case UnitStatus.INSTALLATION: return <FaTools color={colors.LightBlue} />;
    case UnitStatus.MODERNIZATION:
    case UnitStatus.FORCED_UNINSTALL:
      return <ModernizationUnitIcon color={colors.LightBlue} />;
    default: return null;
  }
}

const StatusCell = (unitName: string, production: string): ReactElement => {
  if (production === UnitStatus.OPERATION) {
    return <div />;
  }

  return (
    <>
      <ReactTooltip
        id={`tooltip-${unitName}`}
        place="top"
        border
        textColor="#000000"
        backgroundColor="rgba(255, 255, 255, 0.97)"
        borderColor="#202370"
      >
        <TooltipContainer>
          <strong>
            {handleUnitStatusString(production)}
          </strong>
        </TooltipContainer>
      </ReactTooltip>
      <TableItemCell data-tip data-for={`tooltip-${unitName}`}>
        {handleUnitStatusIcon(production)}
      </TableItemCell>
    </>
  );
};

type TListFilters = 'estado' | 'cidade' | 'unidade' | 'cliente' | 'statusOperacao' | 'data' | 'grupo';

export const Units = (): JSX.Element => {
  const { t } = useTranslation();
  const csvLinkEl = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null);
  const history = useHistory<FilterStateRoute | undefined>();

  const profile = getUserProfile();
  const [showIntroRemoteControl, setShowIntroRemoteControl] = useState(false);

  const [state, render, setState] = useStateVar(() => {
    const state = {
      searchState: [] as { id: string, text: string }[],
      searchValue: '' as string,
      tablePage: 1,
      tablePageSize: 30,
      totalItems: 0,
      lastPageRequestTs: 0,
      isExpanded: false,
      profile,
      orderBy: null as null | [string, 'ASC' | 'DESC'],
      unitsList: [] as {
        hide: boolean
        UNIT_ID: number
        UNIT_NAME: string
        highQtd: number
        goodQtd: number
        lowQtd: number
        offQtd: number
        redHealth: number
        yellowHealth: number
        orangeHealth: number
        greenHealth: number
        greyHealth: number
        blackHealth: number
        dacs: {
          DEV_ID: string
          H_INDEX: number
          insufDutId?: string
          insufDut?: {
            DEV_ID: string
            temprtAlert: 'low'|'high'|'good'|null
            Temperature: number
          }
        }[]
        duts: {
          DEV_ID: string
          temprtAlert: 'low'|'high'|'good'|null
          Temperature: number
          ISVISIBLE: number|null
          eCO2: number
          PLACEMENT: 'AMB'|'INS'|'DUO'
        }[]
        vavs: {
          DEV_ID: string,
          GROUP_NAME?: string,
          GROUP_ID?: number,
          ROOM_NAME: string,
          ISVISIBLE?: number|null,
          Temperature?: number,
          isAuto?: boolean
          temprtAlert: 'low'|'high'|'good'|null,
          status?: string | null,
        }[]
        nobreaks: {
          DAT_CODE: string,
          DMT_CODE: string,
          NOMINAL_POTENTIAL: number,
          NOBREAK_NAME: string,
          NOMINAL_BATERY_LIFE: number,
          INPUT_VOLTAGE: number,
          OUTPUT_VOLTAGE: number
        }[],
        chillers: {
          VARSCFG: string;
          DEVICE_ID: string;
          DEVICE_CODE: string;
        }[],
      }[],
      unitlistfilter: [] as { value: string, name: string }[],
      selectedUnit: setValueState('filterUnit'),
      isLoading: !profile.manageAllClients && !profile.manageSomeClient,
      csvData: [] as { [k: string]: string }[],
      dielClientId: undefined as undefined | number,
      selectedConnection: [],
      manufacturers: [] as {}[],
      ownershipFilter: null as null | { value: string, label: string },
      filters: [] as FilterItem[],
      statesList: [] as { value: string, name: string }[],
      selectedState: setValueState('filterStates'),
      citiesList: [] as { value: string, name: string }[],
      selectedCity: setValueState('filterCity'),
      clientIds: [] as { value: string, name: string }[],
      selectedClientFilter: setValueState('filterClient'),
      selectedStatus: '' as string,
      startOperation: '' as string,
      endOperation: '' as string,
      isUnits: true as boolean,
      tomorrow: moment(moment().add(1, 'days').format('YYYY-MM-DD')),
      openExportList: false as boolean,
      CSVHeader: [] as any,
      selectedTimeRange: 'Flexível' as string,
      needFilter: profile.manageAllClients || profile.manageSomeClient || profile.permissions.isAdminConta,
      includeInstallationUnits: !!profile.manageAllClients || !!profile.permissions.isInstaller || !!profile.permissions.isAdminConta,
      needUnitStatusFilter: false,
      hasAccessManage: !!profile.manageAllClients || !!profile.permissions.isAdminConta,
    };

    return state;
  });

  const [informationsUnits, setInformationsUnits] = useState({
    informationsUnitsCheck: {
      checkState: true as boolean,
      checkCity: true as boolean,
      checkUnit: true as boolean,
      checkStatus: true as boolean,
      checkMachines: true as boolean,
      checkEnvironments: true as boolean,
      checkUtilities: true as boolean,
      checkStartMonitoring: true as boolean,
      checkWaterDevices: true as boolean,
      checkEnergyDevices: true as boolean,
      checkAutomationDevices: true as boolean,
      checkMonitoringDevices: true as boolean,
    },
    statusUnitsCheck: UnitStatus.ALL as UnitStatus | undefined,
    dates: {
      dateStart: null as Moment | null,
      dateEnd: null as Moment | null,
      focusedInput: null as 'endDate' | 'startDate' | null,

    },
    modeExportList: '' as string,
  });
  const [readyToDownload, setReadyToDownload] = useState(false);

  const firstRender = useRef<boolean>(false);

  const formatDate = (dateString) => {
    if (dateString) {
      return `${dateString.slice(8, 10)}/${dateString.slice(5, 7)}/${dateString.slice(0, 4)}`;
    }
  };

  const [listFilters, setListFilters] = useState<TListFilters[]>([
    'estado',
    'cidade',
    'unidade',
    'cliente',
    'grupo',
  ]);

  const [columns, setColumns] = useState([
    {
      Header: (
        <ExpandAll
          onClick={() => {
            state.unitsList.forEach((unit) => {
              unit.hide = !state.isExpanded;
            });
            state.isExpanded = !state.isExpanded;
            render();
          }}
        >
          { state.isExpanded ? (<ArrowUpIcon />) : <ArrowDownIcon /> }
        </ExpandAll>
      ),
      accessor: 'UNIT_ID',
      Cell: (props) => (
        <ExpandBtn
          onClick={() => { state.unitsList.forEach((unit) => {
            if (unit.UNIT_ID === props.UNIT_ID) {
              unit.hide = !unit.hide;
            }
          });
          render();
          }}
        >
          { props.hide ? <ArrowUpIcon /> : <ArrowDownIcon />}
          { UnitIcon({ color: '#363BC4' }) }
        </ExpandBtn>
      ),
    },
    {
      Header: t('estado'),
      accessor: 'col_STATE_ID',
      Cell: (props) => <StyledSpan>{props.STATE_ID}</StyledSpan>,
      HeaderCell: orderColumn,
    },
    {
      Header: t('cidade'),
      accessor: 'col_CITY_NAME',
      Cell: (props) => <StyledSpan>{props.CITY_NAME}</StyledSpan>,
      HeaderCell: orderColumn,
    },
    {
      Header: t('unidade'),
      accessor: 'col_UNIT_NAME',
      Cell: (props) => (
        <StyledLink to={`/analise/unidades/${props.chillers?.length > 0 && !props.hasChillerAndOtherMachines ? 'cag/' : ''}${props.UNIT_ID}`}>
          {props.UNIT_NAME}
        </StyledLink>
      ),
      HeaderCell: orderColumn,
    },
    {
      Header: t('indicesDeSaude'),
      accessor: 'col_health_index',
      HeaderCell: orderColumn,
      Cell: (props) => (
        <Flex flexDirection="column" padding="10px">
          <RowHealth>
            { (props.greenHealth > 0) && (
            <TempColor>
              <NewUnitsHealthIcon H_INDEX={100} />
              { props.greenHealth }
            </TempColor>
            ) }
            { (props.yellowHealth > 0) && (
            <TempColor>
              <NewUnitsHealthIcon H_INDEX={75} />
              { props.yellowHealth }
            </TempColor>
            ) }
            { (props.orangeHealth > 0) && (
              <TempColor>
                <NewUnitsHealthIcon H_INDEX={50} />
                { props.orangeHealth }
              </TempColor>
            )}
            { (props.redHealth > 0) && (
            <TempColor>
              <NewUnitsHealthIcon H_INDEX={25} />
              <div>
                { props.redHealth }
              </div>
            </TempColor>
            ) }
            { (props.blackHealth > 0) && (
            <TempColor>
              <NewUnitsHealthIcon H_INDEX={4} />
              { props.blackHealth }
            </TempColor>
            )}
            { (props.greyHealth > 0) && (
            <TempColor>
              <NewUnitsHealthIcon H_INDEX={999} />
              { props.greyHealth }
            </TempColor>
            ) }
          </RowHealth>
        </Flex>
      ),
    },
    {
      Header: t('temperaturas'),
      accessor: 'col_temperature',
      HeaderCell: orderColumn,
      Cell: (props) => (
        <Flex flexDirection="column">
          <RowTemp>
            { (props.goodQtd > 0) && (
            <TempColor>
              <svg width="15" height="15" style={{ borderRadius: '3px' }}>
                <rect width="15" height="15" style={{ fill: '#5AB365' }} />
              </svg>
              <div>
                { props.goodQtd }
              </div>
            </TempColor>
            ) }
            { (props.lowQtd > 0) && (
            <TempColor>
              <svg width="15" height="15" style={{ borderRadius: '3px' }}>
                <rect width="15" height="15" style={{ fill: '#2D81FF' }} />
              </svg>
              <div>
                { props.lowQtd }
              </div>
            </TempColor>
            ) }
            { (props.highQtd > 0) && (
            <TempColor>
              <svg width="15" height="15" style={{ borderRadius: '3px' }}>
                <rect width="15" height="15" style={{ fill: '#FF0000' }} />
              </svg>
              <div>
                { props.highQtd }
              </div>
            </TempColor>
            ) }
            { (props.offQtd > 0) && (
            <TempColor>
              <svg width="15" height="15" style={{ borderRadius: '3px' }}>
                <rect width="15" height="15" style={{ fill: '#BBBBBB' }} />
              </svg>
              <div>
                { props.offQtd }
              </div>
            </TempColor>
            ) }
          </RowTemp>
        </Flex>
      ),
    },
  ]);

  function formatTotal(value, isCharge, decimalPlaces) {
    if (isCharge) {
      return `R$ ${formatNumberWithFractionDigits(value, { minimum: decimalPlaces, maximum: decimalPlaces })}`;
    }
    return `${formatNumberWithFractionDigits(value, { minimum: 0, maximum: 0 })} kWh`;
  }

  function handleCSVHeader() {
    if (profile.manageAllClients) state.CSVHeader.push({ label: t('cliente'), key: 'client' });
    if (informationsUnits.modeExportList === 'complete') { handleCSVHeaderComplete(); }
    else if (informationsUnits.modeExportList === 'custom') { handleCSVHeaderCustom(); }

    render();
  }

  function handleCSVHeaderComplete() {
    state.CSVHeader.push(
      { label: t('estado'), key: 'state' },
      { label: t('cidade'), key: 'city' },
      { label: t('unidade'), key: 'unit' },
    );

    if (state.needUnitStatusFilter) {
      state.CSVHeader.push(
        { label: t('status'), key: 'status' },
      );
    }

    if (profile.manageAllClients) {
      state.CSVHeader.push(
        { label: t('inicioOperacao'), key: 'startOperation' },
      );
    }

    state.CSVHeader.push(
      { label: t('ambientes'), key: 'environment' },
      { label: t('maquinas'), key: 'machines' },
      { label: t('utilitarios'), key: 'utilities' },
      { label: t('dispositivosAgua'), key: 'waterDevices' },
      { label: t('dispositivosEnergia'), key: 'energyDevices' },
      { label: t('dispositivosAutomacao'), key: 'automationDevices' },
      { label: t('dispositivosMonitoramento'), key: 'monitoringDevices' },
    );
  }

  function handleCSVHeaderCustom() {
    if (informationsUnits.informationsUnitsCheck.checkState) state.CSVHeader.push({ label: t('estado'), key: 'state' });
    if (informationsUnits.informationsUnitsCheck.checkCity) state.CSVHeader.push({ label: t('cidade'), key: 'city' });
    if (informationsUnits.informationsUnitsCheck.checkUnit) state.CSVHeader.push({ label: t('unidade'), key: 'unit' });
    if (informationsUnits.informationsUnitsCheck.checkStatus && state.needUnitStatusFilter) state.CSVHeader.push({ label: t('status'), key: 'status' });
    if (informationsUnits.informationsUnitsCheck.checkStartMonitoring && profile.manageAllClients) state.CSVHeader.push({ label: t('inicioOperacao'), key: 'startOperation' });
    if (informationsUnits.informationsUnitsCheck.checkEnvironments) state.CSVHeader.push({ label: t('ambientes'), key: 'environment' });
    if (informationsUnits.informationsUnitsCheck.checkMachines) state.CSVHeader.push({ label: t('maquinas'), key: 'machines' });
    if (informationsUnits.informationsUnitsCheck.checkUtilities) state.CSVHeader.push({ label: t('utilitarios'), key: 'utilities' });
    if (informationsUnits.informationsUnitsCheck.checkWaterDevices) state.CSVHeader.push({ label: t('dispositivosAgua'), key: 'waterDevices' });
    if (informationsUnits.informationsUnitsCheck.checkEnergyDevices) state.CSVHeader.push({ label: t('dispositivosEnergia'), key: 'energyDevices' });
    if (informationsUnits.informationsUnitsCheck.checkAutomationDevices) state.CSVHeader.push({ label: t('dispositivosAutomacao'), key: 'automationDevices' });
    if (informationsUnits.informationsUnitsCheck.checkMonitoringDevices) state.CSVHeader.push({ label: t('dispositivosMonitoramento'), key: 'monitoringDevices' });
  }

  const updateCheckInformations = (info, date) => {
    switch (info) {
      case 'estado': setInformationsUnits({ ...informationsUnits, informationsUnitsCheck: { ...informationsUnits.informationsUnitsCheck, checkState: !informationsUnits.informationsUnitsCheck.checkState } });
        break;
      case 'cidade': setInformationsUnits({ ...informationsUnits, informationsUnitsCheck: { ...informationsUnits.informationsUnitsCheck, checkCity: !informationsUnits.informationsUnitsCheck.checkCity } });
        break;
      case 'unidade': setInformationsUnits({ ...informationsUnits, informationsUnitsCheck: { ...informationsUnits.informationsUnitsCheck, checkUnit: !informationsUnits.informationsUnitsCheck.checkUnit } });
        break;
      case 'status': setInformationsUnits({ ...informationsUnits, informationsUnitsCheck: { ...informationsUnits.informationsUnitsCheck, checkStatus: !informationsUnits.informationsUnitsCheck.checkStatus } });
        break;
      case 'maquinas': setInformationsUnits({ ...informationsUnits, informationsUnitsCheck: { ...informationsUnits.informationsUnitsCheck, checkMachines: !informationsUnits.informationsUnitsCheck.checkMachines } });
        break;
      case 'ambientes': setInformationsUnits({ ...informationsUnits, informationsUnitsCheck: { ...informationsUnits.informationsUnitsCheck, checkEnvironments: !informationsUnits.informationsUnitsCheck.checkEnvironments } });
        break;
      case 'utilitarios': setInformationsUnits({ ...informationsUnits, informationsUnitsCheck: { ...informationsUnits.informationsUnitsCheck, checkUtilities: !informationsUnits.informationsUnitsCheck.checkUtilities } });
        break;
      case 'inicioMonitoramento': setInformationsUnits({ ...informationsUnits, informationsUnitsCheck: { ...informationsUnits.informationsUnitsCheck, checkStartMonitoring: !informationsUnits.informationsUnitsCheck.checkStartMonitoring } });
        break;
      case 'dispositivosAgua': setInformationsUnits({ ...informationsUnits, informationsUnitsCheck: { ...informationsUnits.informationsUnitsCheck, checkWaterDevices: !informationsUnits.informationsUnitsCheck.checkWaterDevices } });
        break;
      case 'dispositivosEnergia': setInformationsUnits({ ...informationsUnits, informationsUnitsCheck: { ...informationsUnits.informationsUnitsCheck, checkEnergyDevices: !informationsUnits.informationsUnitsCheck.checkEnergyDevices } });
        break;
      case 'dispositivosAutomacao': setInformationsUnits({ ...informationsUnits, informationsUnitsCheck: { ...informationsUnits.informationsUnitsCheck, checkAutomationDevices: !informationsUnits.informationsUnitsCheck.checkAutomationDevices } });
        break;
      case 'dispositivosMonitoramento': setInformationsUnits({ ...informationsUnits, informationsUnitsCheck: { ...informationsUnits.informationsUnitsCheck, checkMonitoringDevices: !informationsUnits.informationsUnitsCheck.checkMonitoringDevices } });
        break;
      case UnitStatus.FORCED_UNINSTALL: setInformationsUnits({ ...informationsUnits, statusUnitsCheck: UnitStatus.FORCED_UNINSTALL });
        break;
      case UnitStatus.DEACTIVATED: setInformationsUnits({ ...informationsUnits, statusUnitsCheck: UnitStatus.DEACTIVATED });
        break;
      case UnitStatus.ALL: setInformationsUnits({ ...informationsUnits, statusUnitsCheck: UnitStatus.ALL });
        break;
      case UnitStatus.MODERNIZATION: setInformationsUnits({ ...informationsUnits, statusUnitsCheck: UnitStatus.MODERNIZATION });
        break;
      case UnitStatus.OPERATION: setInformationsUnits({ ...informationsUnits, statusUnitsCheck: UnitStatus.OPERATION });
        break;
      case UnitStatus.INSTALLATION: setInformationsUnits({ ...informationsUnits, statusUnitsCheck: UnitStatus.INSTALLATION });
        break;
      case 'complete': setInformationsUnits({ ...informationsUnits, modeExportList: 'complete' });
        break;
      case 'custom': setInformationsUnits({ ...informationsUnits, modeExportList: 'custom' });
        break;
      case 'dateStart':
        if (date) setInformationsUnits({ ...informationsUnits, dates: { ...informationsUnits.dates, dateStart: date } });
        break;
      case 'dateEnd':
        if (date) setInformationsUnits({ ...informationsUnits, dates: { ...informationsUnits.dates, dateEnd: date } });
        break;
      case 'focusedInput':
        if (date) setInformationsUnits({ ...informationsUnits, dates: { ...informationsUnits.dates, focusedInput: date } });
        break;
      default:
        break;
    }

    render();
  };

  function handleUnitStatus(): UnitStatusKey[] {
    if (informationsUnits.statusUnitsCheck === UnitStatus.DEACTIVATED) {
      return ['DEACTIVATED'];
    }

    const unitStatus: UnitStatusKey[] = ['OPERATION', 'MODERNIZATION', 'FORCED_UNINSTALL'];

    if (state.includeInstallationUnits) {
      unitStatus.push('INSTALLATION');
    }

    return unitStatus;
  }

  const descFirstColumns = ['col_health_index', 'col_temperature'];

  function onColumnClick(column: { accessor: string }) {
    const isDescFirst = descFirstColumns.includes(column.accessor);
    const isCurrentColumn = state.orderBy && state.orderBy[0] === column.accessor;

    if (!isCurrentColumn) {
      state.orderBy = [column.accessor, isDescFirst ? 'DESC' : 'ASC'];
    } else if (state.orderBy?.[1] === 'ASC') {
      state.orderBy = [column.accessor, 'DESC'];
    } else {
      state.orderBy = [column.accessor, 'ASC'];
    }
    state.tablePage = 1;
    render();
    handleGetDacs();
  }

  function orderColumn(column: TableColumn) {
    return (
      <OrderableHeaderCell
        onColumnClick={onColumnClick}
        column={column}
        orderBy={state.orderBy}
      />
    );
  }

  const handleGetDacs = async () => {
    try {
      if (state.needFilter !== null) setState({ needFilter: false });
      const now = Date.now();
      state.unitsList = [];
      state.lastPageRequestTs = now;
      setState({ isLoading: true });
      const [{ list, totalItems }] = await Promise.all([
        apiCall('/get-units-list-page', {
          INCLUDE_UNIT_STATUS: handleUnitStatus(),
          status: informationsUnits.statusUnitsCheck,
          startOperation: informationsUnits.dates.dateStart ? informationsUnits.dates.dateStart.format('YYYY-MM-DD 00:00:00') : '',
          endOperation: informationsUnits.dates.dateEnd ? informationsUnits.dates.dateEnd.format('YYYY-MM-DD 23:59:59') : '',
          SKIP: (state.tablePage - 1) * state.tablePageSize,
          LIMIT: state.tablePageSize,
          searchTerms: state.searchState?.length ? state.searchState.map((x) => x.text.toLowerCase()) : undefined,
          cityIds: verifyArrayParameters(state.selectedCity),
          stateIds: verifyArrayParameters(state.selectedState),
          unitIds: verifyArrayParametersWithNumber(state.selectedUnit),
          clientIds: verifyArrayParametersWithNumber(state.selectedClientFilter),
          orderByProp: state.orderBy && state.orderBy[0]?.substring('col_'.length) || undefined,
          orderByDesc: (state.orderBy && state.orderBy[1] === 'DESC') || undefined,
        }),
      ]);
      if (state.lastPageRequestTs !== now) return;

      list.forEach((unit) => unit.duts = unit.duts.filter((dut) => ['AMB', 'DUO'].includes(dut.PLACEMENT)));
      state.unitsList = handleHealthUnit(list);
      state.totalItems = totalItems;

      for (const unit of state.unitsList) {
        for (const dac of unit.dacs) {
          if (!dac.insufDutId) continue;
          dac.insufDut = unit.duts.find((x) => x.DEV_ID === dac.insufDutId);
        }
        unit.duts = unit.duts.filter((dut) => !unit.dacs.some((dac) => dac.insufDut === dut));
      }
    } catch (error) {
      console.log(error);
      toast.error(t('erroDadosUnidades'));
    }
    if (state.needFilter === null) setState({ needFilter: true });
    setState({ isLoading: false });
  };

  function countByHealthIndex(devicesHealth, hIndex) {
    return devicesHealth?.length && devicesHealth.filter((d) => d.H_INDEX === hIndex)?.length;
  }

  function handleHealthUnit(list) {
    const unitsList = list.map((unit) => Object.assign(unit, {
      highQtd: (
        (unit.duts.length && unit.duts.filter((dut) => dut.temprtAlert === 'high').length)
        + (unit.vavs.length && unit.vavs.filter((vav) => vav.temprtAlert === 'high').length)
      ),
      goodQtd: (
        (unit.duts.length && unit.duts.filter((dut) => dut.temprtAlert === 'good').length)
        + (unit.vavs.length && unit.vavs.filter((vav) => vav.temprtAlert === 'good').length)
      ),
      lowQtd: (
        (unit.duts.length && unit.duts.filter((dut) => dut.temprtAlert === 'low').length)
        + (unit.vavs.length && unit.vavs.filter((vav) => vav.temprtAlert === 'low').length)
      ),
      offQtd: (
        (unit.duts.length && unit.duts.filter((dut) => dut.temprtAlert === null).length)
        + (unit.vavs.length && unit.vavs.filter((vav) => vav.temprtAlert === null).length)
      ),
      redHealth: countByHealthIndex(unit.devices_health, 25),
      yellowHealth: countByHealthIndex(unit.devices_health, 75),
      blackHealth: countByHealthIndex(unit.devices_health, 4),
      greyHealth: countByHealthIndex(unit.devices_health, 2),
      greenHealth: countByHealthIndex(unit.devices_health, 100),
      orangeHealth: countByHealthIndex(unit.devices_health, 50),
      hide: false,
    }));

    return unitsList;
  }

  function convertDateFormat(date: string) {
    if (date) { return moment(date).format('DD-MM-YYYY'); }
    return date;
  }

  async function getCsvData() {
    setState({ isLoading: true });
    const { list: energy } = await apiCall('/clients/get-units-with-energy-device', {
      unitIds: verifyArrayParameters(state.selectedUnit)?.map((item) => Number(item)),
      clientIds: verifyArrayParameters(state.selectedClientFilter)?.map((item) => Number(item)),
      INCLUDE_UNIT_STATUS: handleUnitStatus(),
    });

    const mapEnergyByUnit: Record<number, typeof energy[number]> = {};

    for (const item of energy) {
      mapEnergyByUnit[item.UNIT_ID] = item;
    }

    apiCall('/get-units-list-page', {
      INCLUDE_UNIT_STATUS: handleUnitStatus(),
      status: informationsUnits.statusUnitsCheck,
      startOperation: informationsUnits.dates.dateStart ? informationsUnits.dates.dateStart.format('YYYY-MM-DD 00:00:00') : '',
      endOperation: informationsUnits.dates.dateEnd ? informationsUnits.dates.dateEnd.format('YYYY-MM-DD 23:59:59') : '',
      searchTerms: state.searchState.map((x) => x.text.toLowerCase()),
      cityIds: verifyArrayParameters(state.selectedCity),
      stateIds: verifyArrayParameters(state.selectedState),
      unitIds: verifyArrayParametersWithNumber(state.selectedUnit),
      clientIds: verifyArrayParametersWithNumber(state.selectedClientFilter),
      orderByProp: state.orderBy && state.orderBy[0]?.substring('col_'.length) || undefined,
      orderByDesc: (state.orderBy && state.orderBy[1] === 'DESC') || undefined,
    }).then(async ({ list }) => {
      handleCSVHeader();

      const listFilter = [] as typeof list;
      list.forEach((unit) => {
        const updatedUnit = {
          ...unit,
          energyInfo: mapEnergyByUnit[unit.UNIT_ID] ?? null,
        };
        listFilter.push(updatedUnit);
      });

      handleCSVData(listFilter);

      setState({ isLoading: false });
      setReadyToDownload(true);
    });
  }

  function getUtilitiesCSV(unit) {
    return [
      ...(unit.nobreaks || []).map((nobreak) => `${nobreak.NOBREAK_NAME ? nobreak.NOBREAK_NAME : '-'}`),
      ...(unit.illumination || []).map((illumination) => `${illumination.NAME ? illumination.NAME : '-'}`),
    ].join(', ');
  }

  function getWaterDevicesCSV(unit) {
    return unit.dmaId !== '' ? unit.dmaId : '-';
  }

  function getEnergyDevicesCSV(unit) {
    return unit.energyInfo ? unit.energyInfo.devices.map((dme) => `${dme.ID}`).join(', ') : '-';
  }

  function getAutomationDevicesCSV(unit) {
    return unit.automation ? unit.automation.map((automation) => `${automation}`).join(', ') : '-';
  }

  function getMonitoringDevicesCSV(unit) {
    return [
      ...(unit.nobreaks || []).map((nobreak) => `${nobreak.DMT_CODE ? nobreak.DMT_CODE : '-'}`),
      ...(unit.duts.map((dut) => `${dut.DEV_ID}`) || '-'),
    ].join(', ');
  }

  function handleCSVData(listFilter) {
    setState({
      csvData: listFilter.map((unit) => ({
        client: unit.CLIENT_NAME || '-',
        state: unit.STATE_ID || '-',
        city: unit.CITY_NAME || '-',
        unit: unit.UNIT_NAME || '-',
        status: handleUnitStatusString(String(unit.PRODUCTION)),
        startOperation: convertDateFormat(unit.PRODUCTION_TIMESTAMP) || '-',
        environment: unit.duts.filter((dut) => dut.ROOM_NAME).map((dut) => dut.ROOM_NAME).join(', ') || '-',
        machines: unit.machines?.map((machine) => machine.MACHINE_NAME).join(', ') || '-',
        utilities: getUtilitiesCSV(unit),
        waterDevices: getWaterDevicesCSV(unit),
        energyDevices: getEnergyDevicesCSV(unit),
        automationDevices: getAutomationDevicesCSV(unit),
        monitoringDevices: getMonitoringDevicesCSV(unit),
      })),
    });
  }

  function onPageChange(page) {
    state.tablePage = page;
    render();
    handleGetDacs();
  }

  async function verifyNeedUnitStatus(): Promise<void> {
    if (state.hasAccessManage || profile.permissions.isInstaller) {
      setState({
        needUnitStatusFilter: true,
      });
      return;
    }

    const { list } = await apiCall('/clients/get-units-list-basic');

    const needUnitStatusFilter = list.some((item) => [UnitStatus.MODERNIZATION, UnitStatus.FORCED_UNINSTALL, UnitStatus.DEACTIVATED].includes(item.PRODUCTION.toString() as UnitStatus));

    setState({
      needUnitStatusFilter,
    });
  }

  const setFiltersAndList = async () => {
    state.tablePage = 1;
    await handleGetDacs();
  };

  async function clearFilters() {
    state.searchState = [];
    state.selectedCity = [];
    state.selectedState = [];
    state.selectedUnit = [];
    state.selectedClientFilter = [];
    state.selectedStatus = '';
    state.startOperation = '';
    state.endOperation = '';
    setInformationsUnits({
      ...informationsUnits,
      statusUnitsCheck: UnitStatus.ALL,
    });

    state.isLoading = true;
    render();
    await handleGetDacs();
  }

  function onCloseIntroRemoteControlModal(): void {
    apiCall('/users/edit-user', {
      USER: profile?.user,
      PREFS: JSON.stringify({
        remoteControl: ['intro'],
      }),
    }).then((response) => {
      if (response) {
        refreshUserProfile();
      }
    });

    setShowIntroRemoteControl(false);
  }

  useEffect(() => {
    if (state.needUnitStatusFilter && !firstRender.current) {
      firstRender.current = true;

      setColumns((previousValues) => {
        const newArray = [...previousValues];

        newArray.splice(3, 0, {
          Header: t('status'),
          accessor: 'col_PRODUCTION',
          Cell: (props) => StatusCell(props.UNIT_NAME, String(props.PRODUCTION)),
          HeaderCell: orderColumn,
        });

        return newArray;
      });

      setListFilters((previousValues) => [...previousValues, 'statusOperacao']);
    }
  }, [state.needUnitStatusFilter]);

  useEffect(() => {
    if (!profile.manageAllClients && !profile.manageSomeClient) {
      setFiltersAndList();
    }
  }, []);

  useEffect(() => {
    if (state.hasAccessManage) {
      setListFilters((previousValues) => [...previousValues, 'data']);
      setColumns((previousValues) => {
        const prevValues = previousValues.filter((item) => !['col_CLIENT_NAME', 'col_PRODUCTION_TIMESTAMP'].includes(item.accessor));

        const newArray = [
          {
            Header: t('cliente'),
            accessor: 'col_CLIENT_NAME',
            Cell: (props) => <StyledSpan>{props.CLIENT_NAME}</StyledSpan>,
            HeaderCell: orderColumn,
          },
          ...prevValues];

        newArray.splice(3, 0,
          {
            Header: t('inicioOperacao'),
            accessor: 'col_PRODUCTION_TIMESTAMP',
            Cell: (props) => <StyledSpan>{formatDate(props.PRODUCTION_TIMESTAMP)}</StyledSpan>,
            HeaderCell: orderColumn,
          });

        return newArray;
      });
    }

    verifyNeedUnitStatus();
  }, []);

  useEffect(() => {
    if (profile.permissions.isPersonaII) {
      const parsedPrefs = JSON.parse(profile?.prefs ?? '{}');
      const introRemoteControl = parsedPrefs.remoteControl;
      if (!introRemoteControl || !introRemoteControl.includes('intro')) {
        setTimeout(() => {
          setShowIntroRemoteControl(true);
        }, 500);
      }
    }
  }, []);

  useEffect(() => {
    if (readyToDownload && csvLinkEl.current) {
      csvLinkEl.current.link.click();
      setReadyToDownload(false);
      state.CSVHeader = [];
    }
  }, [readyToDownload, state.csvData, state.CSVHeader]);

  useEffect(() => {
    handleOverviewStateFilters(
      history.location.state,
      state,
      render,
      'unidades',
    );

    history.location.state = undefined;
  }, []);

  return (
    <>
      <IntroRemoteControlModal
        isOpen={showIntroRemoteControl}
        onClose={onCloseIntroRemoteControlModal}
      />
      <Helmet>
        <title>{t('dielEnergiaUnidades')}</title>
      </Helmet>
      <AnalysisLayout />
      <UtilFilter
        state={state}
        render={render}
        onAply={setFiltersAndList}
        updateCheckInformations={updateCheckInformations}
        informationsUnits={informationsUnits}
        clearFilters={clearFilters}
        exportFunc={getCsvData}
        csvHeader={state.CSVHeader}
        listFilters={listFilters}
        lengthArrayResult={state.totalItems}
      />
      <Box width={[1, 1, 1, 1, 1 / 4, 1 / 5]} mb={[24, 24, 24, 24, 24, 0]} minWidth={280}>
        <CSVLink
          headers={state.CSVHeader}
          data={state.csvData}
          separator=";"
          enclosingCharacter="'"
          filename={t('ListagemDeUnidadesCsv')}
          ref={csvLinkEl}
        />
      </Box>
      {state.needFilter && (
      <AnalysisEmpty />
      )}
      {state.isLoading
        ? (
          <EmptyWrapper>
            <Loader variant="primary" size="large" />
          </EmptyWrapper>
        )
        : (!state.needFilter && (
          <DesktopTable
            style={{
              paddingTop: 16,
            }}
            verifyMobile
          >
            {state.unitsList
              ? (
                <DataTable
                  isUnit
                  columns={columns}
                  data={state.unitsList}
                  onPageChange={onPageChange}
                  currentPage={state.tablePage}
                  pageSize={state.tablePageSize}
                  totalItems={state.totalItems}
                  verifyMobile
                />
              )
              : (
                <Flex justifyContent="center" alignItems="center">
                  <Box justifyContent="center" alignItems="center">
                    <StyledSpan>{t('erroDados')}</StyledSpan>
                  </Box>
                </Flex>
              )}
          </DesktopTable>
        ))}
    </>
  );
};

export default withTransaction('Units', 'component')(Units);
